/**
 * Table-driven case data for the address / region / matcher suites — T1.1c.
 *
 * Single source of truth shared by two runners:
 *   - `*.spec.ts`   → Jest (runs once the scaffold's test runner lands),
 *   - `selfcheck.ts` → `npx --yes tsx packages/shared/src/address/selfcheck.ts` (DoD today).
 *
 * PROVENANCE OF THE 48-CASE TABLE — read before auditing the merge-rate number.
 *
 * docs/02 §10.3 documents the address variance CLASSES with a handful of literal strings
 * and gives the aggregate counts (31/48 pairs equal raw, 36/48 after «NFKC + дефисы + lower
 * + пробелы», ЕПГУ8 ↔ КвотыПоПл 45/45). The source CSVs themselves are NOT in the repo
 * (05 §7: real raw files stay in gitignored `docs/artifacts/real/`) and were no longer in
 * `~/Downloads` when T1.1c was written, so the 48 rows below are a RECONSTRUCTION: every
 * literal string that docs/02 quotes is copied verbatim and marked `02 §…`; the remaining
 * rows apply one of the six documented variance classes to the same eight venues and are
 * marked `reconstructed(class …)`. Nothing is invented beyond the classes the doc names.
 *
 * Consequence: the ≥45/48 AC is evaluated against this reconstruction. The number that
 * matters for the real warm-up gate is the one D2 reproduces from the raw files — if the
 * real 48 behave differently, the delta belongs in `docs/warmup-report.md` (05 §9.3).
 */
/**
 * The provenance note the suites assert on — keeps the reconstruction honest in the test
 * output, not only in this file's header.
 */
export const ADDRESS_CASES_DOC_PROVENANCE_NOTE = '48-case address set is a RECONSTRUCTION of the docs/02 §10.3 variance classes: ' +
    'verbatim rows cite «02 §…», the rest replay one documented class on the same venue ' +
    '(the source CSVs are not in the repo — see docs/05 §7 and cases.ts header).';
/* ------------------------------------------------------------------ *
 * Venues — every street/city/postal below is quoted in docs/02.
 * ------------------------------------------------------------------ */
const NAME_MBOU_SOSH = 'Муниципальное бюджетное общеобразовательное учреждение «Средняя общеобразовательная школа № 8»';
export const VENUES = Object.freeze([
    {
        venueId: 1,
        label: 'Шипиловский 37 к2 (Москва)',
        name: 'Государственное бюджетное общеобразовательное учреждение города Москвы «Школа № 878».',
        address: '115569, ул.Шипиловский, д.37 корпус 2, Москва',
        nameIsDocumented: true, // docs/02 §2, «Площадка» example
    },
    {
        venueId: 2,
        label: '1-я Аэропортовская 1 (Москва)',
        name: 'Синтетическая площадка 2 (адрес из docs/02 §10.3.2, имя не документировано)',
        address: '125319, ул.1‑я Аэропортовская, д.1, Москва',
        nameIsDocumented: false,
    },
    {
        venueId: 3,
        label: 'Горно-алтайская 19 (Горно-Алтайск)',
        name: 'Синтетическая площадка 3 (адрес из docs/02 §10.3.6, имя не документировано)',
        address: '649002, ул.Горно‑алтайская, д.19, Горно-Алтайск, Республика Алтай',
        nameIsDocumented: false,
    },
    {
        venueId: 4,
        label: 'Центральная 33, Долгое Ледово',
        name: 'Синтетическая площадка 4 (адрес из docs/02 §10.3.2, имя не документировано)',
        address: '141143, ул.Центральная, д.33, Долгое Ледово, Московская область',
        nameIsDocumented: false,
    },
    {
        venueId: 5,
        label: 'Мира 10 (Альметьевск)',
        name: 'Государственное автономное профессиональное образовательное учреждение «Альметьевский политехнический техникум»',
        address: '423457, ул.Мира, д.10, Альметьевск, Республика Татарстан',
        nameIsDocumented: true, // docs/02 §5, «Площадка» example
    },
    {
        venueId: 6,
        label: 'Стахановская 27 (Кемерово)',
        name: 'Синтетическая площадка 6 (адрес из docs/02 §10.3.4, имя не документировано)',
        address: '650903, ул.Стахановская, д.27, Кемерово, Кемеровская область - Кузбасс',
        nameIsDocumented: false,
    },
    {
        venueId: 7,
        label: 'мкр.Красная горка 29 (Топки)',
        name: NAME_MBOU_SOSH,
        address: '652305, мкр.Красная горка, д.29, Топки, Кемеровская область - кузбасс',
        nameIsDocumented: true, // docs/02 §6, «Название организации…» example
    },
    {
        venueId: 8,
        label: 'Доброхотова 8 (Ярославль)',
        name: 'Синтетическая площадка 8 (адрес из docs/02 §7, имя не документировано)',
        address: '150062, ул.Доброхотова, д.8, Ярославль, Ярославская область',
        nameIsDocumented: false,
    },
]);
/* ------------------------------------------------------------------ *
 * The 48-case address set (8 venues x 6 documented variance classes).
 * ------------------------------------------------------------------ */
/**
 * docs/02 §10.3 numbered classes, replayed per venue:
 *   1 корпус / к. / missing space   2 register inside the address
 *   3 official+colloquial in brackets, three registers
 *   4 full vs short region form     5 «г.» prefix on the city/region
 *   6 U+2011 vs ASCII hyphen        (plus the §10.3.1 duplicated-city trap)
 */
export const ADDRESS_48_CASES = Object.freeze([
    // ---- venue 1 — Шипиловский (the doc's own worked example, §10.3.1) -------------
    {
        id: 'V1-01',
        venueId: 1,
        venue: 'Шипиловский',
        raw: '115569, ул.Шипиловский, д.37 корпус 2, Москва',
        provenance: { kind: 'verbatim', doc: '02 §2 «Адрес» / §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V1-02',
        venueId: 1,
        venue: 'Шипиловский',
        raw: '115569, ул.Шипиловский, д.37, к.2, Москва',
        provenance: { kind: 'verbatim', doc: '02 §4 ЕВИ «Адрес площадки» / §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V1-03',
        venueId: 1,
        venue: 'Шипиловский',
        raw: '115569, ул.Шипиловский, д.37,к.2, Москва',
        provenance: { kind: 'verbatim', doc: '02 §10.3.1 (пропущен пробел после запятой)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V1-04',
        venueId: 1,
        venue: 'Шипиловский',
        raw: '115569, ул.Шипиловский, д.37 корпус 2, Москва, Москва',
        provenance: { kind: 'verbatim', doc: '02 §10.3.1 (Квоты, город задублирован)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V1-05',
        venueId: 1,
        venue: 'Шипиловский',
        raw: '115569, ул.Шипиловский, д.37, к.2, г. Москва',
        provenance: { kind: 'reconstructed', klass: '5 «г.»-prefix', doc: '02 §10.3.5' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V1-06',
        venueId: 1,
        venue: 'Шипиловский',
        raw: '115569, ул.шипиловский, д.37, корпус 2, Москва',
        provenance: { kind: 'reconstructed', klass: '2 register', doc: '02 §10.3.2' },
        expectedTier: 'EXACT_ADDRESS',
    },
    // ---- venue 2 — 1-я Аэропортовская (U+2011, §10.3.2 + §10.3.6) ------------------
    {
        id: 'V2-01',
        venueId: 2,
        venue: 'Аэропортовская',
        raw: '125319, ул.1‑я Аэропортовская, д.1, Москва',
        provenance: { kind: 'verbatim', doc: '02 §10.3.2 (ЕПГУ 8.09, Квоты по площадкам)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V2-02',
        venueId: 2,
        venue: 'Аэропортовская',
        raw: '125319, ул.1‑я аэропортовская, д.1, Москва, Москва',
        provenance: { kind: 'verbatim', doc: '02 §10.3.2 (Квоты)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V2-03',
        venueId: 2,
        venue: 'Аэропортовская',
        raw: '125319, ул.1-я Аэропортовская, д.1, Москва',
        provenance: { kind: 'reconstructed', klass: '6 ASCII hyphen for U+2011', doc: '02 §10.3.6' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V2-04',
        venueId: 2,
        venue: 'Аэропортовская',
        raw: '125319, ул.1‑я Аэропортовская, д.1, г. Москва',
        provenance: { kind: 'reconstructed', klass: '5 «г.»-prefix', doc: '02 §10.3.5' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V2-05',
        venueId: 2,
        venue: 'Аэропортовская',
        raw: '125319, ул. 1‑я Аэропортовская, д. 1, Москва',
        provenance: { kind: 'reconstructed', klass: '1 пробел после сокращения', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V2-06',
        venueId: 2,
        venue: 'Аэропортовская',
        raw: '125319, ул.1‑Я АЭРОПОРТОВСКАЯ, д.1, МОСКВА',
        provenance: { kind: 'reconstructed', klass: '2 register (caps)', doc: '02 §10.3.2' },
        expectedTier: 'EXACT_ADDRESS',
    },
    // ---- venue 3 — Горно-алтайская (the second U+2011 street, §10.3.6) -------------
    {
        id: 'V3-01',
        venueId: 3,
        venue: 'Горно-алтайская',
        raw: '649002, ул.Горно‑алтайская, д.19, Горно-Алтайск, Республика Алтай',
        provenance: { kind: 'verbatim', doc: '02 §10.3.6 (улица); дом/город реконструированы' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V3-02',
        venueId: 3,
        venue: 'Горно-алтайская',
        raw: '649002, ул.Горно-алтайская, д.19, Горно-Алтайск, Республика Алтай',
        provenance: { kind: 'reconstructed', klass: '6 ASCII hyphen for U+2011', doc: '02 §10.3.6' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V3-03',
        venueId: 3,
        venue: 'Горно-алтайская',
        raw: '649002, ул.Горно‑Алтайская, д.19, Горно-Алтайск, республика алтай (алтай)',
        provenance: {
            kind: 'reconstructed',
            klass: '3 скобки + регистр',
            doc: '02 §10.3.3',
        },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V3-04',
        venueId: 3,
        venue: 'Горно-алтайская',
        raw: '649002, ул.Горно‑алтайская, д.19, г. Горно-Алтайск, Республика Алтай',
        provenance: { kind: 'reconstructed', klass: '5 «г.»-prefix', doc: '02 §10.3.5' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V3-05',
        venueId: 3,
        venue: 'Горно-алтайская',
        raw: '649002, ул. горно‑алтайская, д.19, горно-алтайск, республика алтай',
        provenance: { kind: 'reconstructed', klass: '1+2 пробел и регистр', doc: '02 §10.3.1/2' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V3-06',
        venueId: 3,
        venue: 'Горно-алтайская',
        raw: '649002, ул.Горно‑алтайская, д.19, Горно-Алтайск, Республика Алтай, Республика Алтай',
        provenance: { kind: 'reconstructed', klass: 'дублирован сегмент', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    // ---- venue 4 — Долгое Ледово (register of the settlement name, §10.3.2) --------
    {
        id: 'V4-01',
        venueId: 4,
        venue: 'Долгое Ледово',
        raw: '141143, ул.Центральная, д.33, Долгое Ледово, Московская область',
        provenance: { kind: 'verbatim', doc: '02 §10.3.2 (ЕВИ)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V4-02',
        venueId: 4,
        venue: 'Долгое Ледово',
        raw: '141143, ул.Центральная, д.33, Долгое ледово, Московская область',
        provenance: { kind: 'verbatim', doc: '02 §10.3.2 (Квоты)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V4-03',
        venueId: 4,
        venue: 'Долгое Ледово',
        raw: '141143, ул.центральная, д.33, долгое ледово, московская область',
        provenance: { kind: 'reconstructed', klass: '2 register (весь адрес)', doc: '02 §10.3.2' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V4-04',
        venueId: 4,
        venue: 'Долгое Ледово',
        raw: '141143, ул.Центральная, д.33, Долгое Ледово, Московская область, Московская область',
        provenance: { kind: 'reconstructed', klass: 'дублирован сегмент', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V4-05',
        venueId: 4,
        venue: 'Долгое Ледово',
        raw: '141143, ул.Центральная, д.33 , Долгое Ледово, Московская область',
        provenance: { kind: 'reconstructed', klass: '1 пробел перед запятой', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V4-06',
        venueId: 4,
        venue: 'Долгое Ледово',
        raw: '141143, ул.Центральная, д.33, Долгое Ледово, Московская область (московская область)',
        provenance: { kind: 'reconstructed', klass: '3 скобки-дубль региона', doc: '02 §10.3.3' },
        expectedTier: 'EXACT_ADDRESS',
    },
    // ---- venue 5 — Альметьевск (Татарстан in three registers, §10.3.3) -------------
    {
        id: 'V5-01',
        venueId: 5,
        venue: 'Альметьевск',
        raw: '423457, ул.Мира, д.10, Альметьевск, Республика татарстан (татарстан)',
        provenance: { kind: 'verbatim', doc: '02 §5 «На зачислении» / §10.3.3' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V5-02',
        venueId: 5,
        venue: 'Альметьевск',
        raw: '423457, ул.Мира, д.10, Альметьевск, Республика Татарстан',
        provenance: { kind: 'verbatim', doc: '02 §4 ЕВИ «Адрес площадки»' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V5-03',
        venueId: 5,
        venue: 'Альметьевск',
        raw: '423457, ул.Мира, д.10, Альметьевск, Республика Татарстан (Татарстан)',
        provenance: { kind: 'verbatim', doc: '02 §10.3.3 (ЕВИ, 29 вхождений)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V5-04',
        venueId: 5,
        venue: 'Альметьевск',
        raw: '423457, ул.Мира, д.10, Альметьевск, республика татарстан (татарстан)',
        provenance: { kind: 'verbatim', doc: '02 §10.3.3 (ЕПГУ 8.09, 170 вхождений)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V5-05',
        venueId: 5,
        venue: 'Альметьевск',
        raw: '423457, ул.Мира, д.10, Альметьевск, Альметьевск, Республика Татарстан',
        provenance: { kind: 'reconstructed', klass: 'дублирован город', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V5-06',
        venueId: 5,
        venue: 'Альметьевск',
        raw: '423457, ул.Мира, д.10, г. Альметьевск, Республика Татарстан',
        provenance: { kind: 'reconstructed', klass: '5 «г.»-prefix', doc: '02 §10.3.5' },
        expectedTier: 'EXACT_ADDRESS',
    },
    // ---- venue 6 — Кемерово (full vs short region form, §10.3.4) -------------------
    {
        id: 'V6-01',
        venueId: 6,
        venue: 'Стахановская',
        raw: '650903, ул.Стахановская, д.27, Кемерово, Кемеровская область',
        provenance: { kind: 'verbatim', doc: '02 §10.3.4 (ЕВИ, короткая форма)' },
        expectedTier: 'FUZZY',
    },
    {
        id: 'V6-02',
        venueId: 6,
        venue: 'Стахановская',
        raw: '650903, ул.Стахановская, д.27, Кемерово, Кемеровская область - кузбасс',
        provenance: { kind: 'verbatim', doc: '02 §10.3.4 (Квоты, внутри адреса)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V6-03',
        venueId: 6,
        venue: 'Стахановская',
        raw: '650903, ул.Стахановская, д.27, Кемерово, Кемеровская область - Кузбасс',
        provenance: { kind: 'verbatim', doc: '02 §10.3.4 (Квоты.Регион, полная форма)' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V6-04',
        venueId: 6,
        venue: 'Стахановская',
        raw: '650903, ул.стахановская, д.27, кемерово, Кемеровская область - Кузбасс',
        provenance: { kind: 'reconstructed', klass: '2 register', doc: '02 §10.3.2' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V6-05',
        venueId: 6,
        venue: 'Стахановская',
        raw: '650903, ул.Стахановская, д.27, г. Кемерово, Кемеровская область - кузбасс',
        provenance: { kind: 'reconstructed', klass: '5 «г.»-prefix', doc: '02 §10.3.5' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V6-06',
        venueId: 6,
        venue: 'Стахановская',
        raw: '650903, ул.Стахановская, д.27, Кемерово, Кемерово, Кемеровская область - Кузбасс',
        provenance: { kind: 'reconstructed', klass: 'дублирован город', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    // ---- venue 7 — Топки (Квоты hand-maintained form, docs/02 §6) ------------------
    {
        id: 'V7-01',
        venueId: 7,
        venue: 'Красная горка',
        raw: '652305, мкр.Красная горка, д.29, Топки, Кемеровская область - кузбасс',
        provenance: { kind: 'verbatim', doc: '02 §6 «Адрес»' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V7-02',
        venueId: 7,
        venue: 'Красная горка',
        raw: '652305, мкр.Красная Горка, д.29, Топки, Кемеровская область - Кузбасс',
        provenance: { kind: 'reconstructed', klass: '2 register', doc: '02 §10.3.2' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V7-03',
        venueId: 7,
        venue: 'Красная горка',
        raw: '652305, мкр. красная горка, д.29, Топки, Кемеровская область',
        provenance: {
            kind: 'reconstructed',
            klass: '1+4 пробел и короткая форма региона',
            doc: '02 §10.3.1/4',
        },
        expectedTier: 'FUZZY',
    },
    {
        id: 'V7-04',
        venueId: 7,
        venue: 'Красная горка',
        raw: '652305, мкр.Красная горка, д.29, г. Топки, Кемеровская область - Кузбасс',
        provenance: { kind: 'reconstructed', klass: '5 «г.»-prefix', doc: '02 §10.3.5' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V7-05',
        venueId: 7,
        venue: 'Красная горка',
        raw: '652305, мкр.Красная горка, д.29, Топки, Кузбасс',
        provenance: {
            kind: 'reconstructed',
            klass: '4 голое разговорное имя региона',
            doc: '02 §10.3.4',
        },
        expectedTier: 'FUZZY',
    },
    {
        id: 'V7-06',
        venueId: 7,
        venue: 'Красная горка',
        raw: '652305, мкр.Красная горка, д.29, Топки, Топки, Кемеровская область - кузбасс',
        provenance: { kind: 'reconstructed', klass: 'дублирован город', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    // ---- venue 8 — Ярославль (docs/02 §7 pivot address) ----------------------------
    {
        id: 'V8-01',
        venueId: 8,
        venue: 'Доброхотова',
        raw: '150062, ул.Доброхотова, д.8, Ярославль, Ярославская область',
        provenance: { kind: 'verbatim', doc: '02 §7 c1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V8-02',
        venueId: 8,
        venue: 'Доброхотова',
        raw: '150062, ул.доброхотова, д.8, Ярославль, ярославская область',
        provenance: { kind: 'reconstructed', klass: '2 register', doc: '02 §10.3.2' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V8-03',
        venueId: 8,
        venue: 'Доброхотова',
        raw: '150062, ул.Доброхотова, д.8, г. Ярославль, Ярославская область',
        provenance: { kind: 'reconstructed', klass: '5 «г.»-prefix', doc: '02 §10.3.5' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V8-04',
        venueId: 8,
        venue: 'Доброхотова',
        raw: '150062, ул.Доброхотова, д.8, Ярославль, Ярославская область, Ярославская область',
        provenance: { kind: 'reconstructed', klass: 'дублирован сегмент', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V8-05',
        venueId: 8,
        venue: 'Доброхотова',
        raw: '150062, ул.Доброхотова, д.8, Ярославль, Ярославская область (ярославская область)',
        provenance: { kind: 'reconstructed', klass: '3 скобки-дубль региона', doc: '02 §10.3.3' },
        expectedTier: 'EXACT_ADDRESS',
    },
    {
        id: 'V8-06',
        venueId: 8,
        venue: 'Доброхотова',
        raw: '150062, ул. Доброхотова , д.8, Ярославль, Ярославская область',
        provenance: { kind: 'reconstructed', klass: '1 пробелы вокруг сокращения', doc: '02 §10.3.1' },
        expectedTier: 'EXACT_ADDRESS',
    },
]);
/* ------------------------------------------------------------------ *
 * Шипиловский ×3 — the task's named merge case (06 T1.1c).
 * ------------------------------------------------------------------ */
export const SHIPILKOVSKY_THREE = Object.freeze(['V1-02', 'V1-03', 'V1-04'].map((id) => {
    const found = ADDRESS_48_CASES.find((addressCase) => addressCase.id === id);
    if (found === undefined)
        throw new Error(`case ${id} missing from ADDRESS_48_CASES`);
    return found;
}));
/** Lookup by id for tests that want one specific row. */
export function addressCaseById(id) {
    const found = ADDRESS_48_CASES.find((addressCase) => addressCase.id === id);
    if (found === undefined)
        throw new Error(`case ${id} missing from ADDRESS_48_CASES`);
    return found;
}
export const REGION_CASES = Object.freeze([
    { raw: 'Республика Татарстан', expected: 'Республика Татарстан', doc: '02 §4 ЕВИ.Регион' },
    {
        raw: 'Республика Татарстан (Татарстан)',
        expected: 'Республика Татарстан',
        doc: '02 §10.3.3 ЕВИ (29)',
    },
    {
        raw: 'Республика татарстан (татарстан)',
        expected: 'Республика Татарстан',
        doc: '02 §10.3.3 ЕПГУ 8.09 (170)',
    },
    {
        raw: 'республика татарстан (татарстан)',
        expected: 'Республика Татарстан',
        doc: '02 §10.3.3 (третий регистр)',
    },
    { raw: 'ТАТАРСТАН', expected: 'Республика Татарстан', doc: '02 §10.3.3 (регистр)' },
    { raw: 'Республика Адыгея (Адыгея)', expected: 'Республика Адыгея', doc: '02 §10.3.3 ЕВИ' },
    { raw: 'Республика адыгея (адыгея)', expected: 'Республика Адыгея', doc: '02 §10.3.3 ЕПГУ' },
    { raw: 'Республика Адыгея', expected: 'Республика Адыгея', doc: '02 §10.3.3 Квоты/ДОП' },
    {
        raw: 'Кемеровская область - Кузбасс',
        expected: 'Кемеровская область - Кузбасс',
        doc: '02 §10.3.4 Квоты.Регион',
    },
    {
        raw: 'Кемеровская область - кузбасс',
        expected: 'Кемеровская область - Кузбасс',
        doc: '02 §10.3.4 внутри адреса',
    },
    {
        raw: 'Кемеровская область',
        expected: 'Кемеровская область - Кузбасс',
        doc: '02 §10.3.4 ЕВИ.Регион',
    },
    { raw: 'Кузбасс', expected: 'Кемеровская область - Кузбасс', doc: '02 §10.3.4 разговорная' },
    { raw: 'КЕМЕРОВСКАЯ ОБЛАСТЬ - КУЗБАСС', expected: 'Кемеровская область - Кузбасс', doc: 'регистр' },
    { raw: 'Москва', expected: 'Москва', doc: '02 §10.3.5 ЕВИ/ЕПГУ' },
    { raw: 'г. Москва', expected: 'Москва', doc: '02 §10.3.5 Квоты/ДОП' },
    { raw: 'г. Санкт-Петербург', expected: 'Санкт-Петербург', doc: '02 §10.3.5' },
    { raw: 'Санкт-Петербург', expected: 'Санкт-Петербург', doc: '02 §10.3.5 без префикса' },
    { raw: 'г. Севастополь', expected: 'Севастополь', doc: '02 §10.3.5' },
    { raw: 'Московская область', expected: 'Московская область', doc: '02 §10.3.2' },
    { raw: 'Ульяновская область', expected: 'Ульяновская область', doc: '02 §5 Регион' },
    { raw: 'Калужская область', expected: 'Калужская область', doc: '02 §1' },
    { raw: 'Ставропольский край', expected: 'Ставропольский край', doc: '02 §1' },
    { raw: 'Ярославская область', expected: 'Ярославская область', doc: '02 §7 c1' },
    { raw: '  Республика Татарстан  ', expected: 'Республика Татарстан', doc: 'пробелы' },
    { raw: '', expected: '', doc: 'пустая ячейка' },
]);
/** Regions docs/02 does NOT enumerate: the dictionary is a seed, D2/D1 extend it. */
export const REGION_UNKNOWN_CASES = Object.freeze([
    { raw: 'Новосибирская область', expected: 'новосибирская область', doc: 'не в docs/02' },
    { raw: 'Ханты-Мансийский Автономный Округ', expected: 'ханты-мансийский автономный округ', doc: 'не в docs/02' },
]);
/** Alias table is built from VENUES; these inputs must NOT merge anywhere. */
export const MATCHER_NEGATIVE_CASES = Object.freeze([
    {
        name: 'different postal code, different venue — nothing to match',
        input: { normalizedAddress: '394000, ул.Пушкинская, д.13, Воронеж, Воронежская область' },
        expected: null,
        expectedVenueId: null,
        reasonIncludes: ['no alias reaches FUZZY_MIN_SIMILARITY'],
    },
    {
        name: 'empty input is refused with a reason',
        input: {},
        expected: null,
        expectedVenueId: null,
        reasonIncludes: ['no input'],
    },
    {
        name: 'a 3-token address is too little evidence for FUZZY',
        input: { normalizedAddress: '115569, ул.Шипиловский' },
        expected: null,
        expectedVenueId: null,
        reasonIncludes: ['no alias reaches FUZZY_MIN_SIMILARITY'],
    },
]);
/**
 * Same-postal near-misses that the guards must reject — they sit ABOVE the 0.85 threshold
 * without the house/street guards, which is exactly why those guards exist.
 */
export const MATCHER_GUARD_CASES = Object.freeze([
    {
        name: 'different house number, same postal/street — rejected by the house guard at 0.875 similarity',
        input: { normalizedAddress: '115569, ул.Шипиловский, д.38 корпус 2, Москва' },
        expected: null,
        expectedVenueId: null,
        reasonIncludes: ['house'],
    },
    {
        name: 'different street, same postal/city/house — rejected by the street guard at 0.875 similarity',
        input: { normalizedAddress: '115569, ул.Другая, д.37 корпус 2, Москва' },
        expected: null,
        expectedVenueId: null,
        reasonIncludes: ['street'],
    },
    {
        name: 'cross-postal near-miss (same street/house, different postal) — rejected by the postal guard',
        input: { normalizedAddress: '115570, ул.Шипиловский, д.37 корпус 2, Москва' },
        expected: null,
        expectedVenueId: null,
        reasonIncludes: ['postal'],
    },
]);
export const MATCHER_TIER_CASES = Object.freeze([
    {
        name: 'EXACT_NAME outranks EXACT_ADDRESS when both would hit different venues',
        input: { normalizedAddress: '423457, ул.Мира, д.10, Альметьевск, Республика Татарстан', venueName: VENUES[0]?.name ?? '' },
        expected: 'EXACT_NAME',
        expectedVenueId: 1,
    },
    {
        name: 'EXACT_ADDRESS hits on the ЕПГУ 8.09 master form',
        input: { normalizedAddress: VENUES[0]?.address ?? '' },
        expected: 'EXACT_ADDRESS',
        expectedVenueId: 1,
    },
    {
        name: 'name alone, in another register, is still EXACT_NAME',
        input: { venueName: 'ГОСУДАРСТВЕННОЕ БЮДЖЕТНОЕ ОБЩЕОБРАЗОВАТЕЛЬНОЕ УЧРЕЖДЕНИЕ ГОРОДА МОСКВЫ «ШКОЛА № 878».' },
        expected: 'EXACT_NAME',
        expectedVenueId: 1,
    },
    {
        name: 'short truncated address still fuzzy-merges inside the same postal code',
        input: { normalizedAddress: '115569, ул.Шипиловский, д.37 корпус 2' },
        expected: 'FUZZY',
        expectedVenueId: 1,
    },
    {
        name: 'city is NOT guarded: same postal+street+house with another city still merges (city is the noisiest field in docs/02)',
        input: { normalizedAddress: '115569, ул.Шипиловский, д.37 корпус 2, Химки' },
        expected: 'FUZZY',
        expectedVenueId: 1,
    },
]);
/** Idempotency corpus — every literal address docs/02 quotes, plus the reconstructed rows. */
export const IDEMPOTENCY_CORPUS = Object.freeze([
    ...VENUES.map((venue) => venue.address),
    ...ADDRESS_48_CASES.map((addressCase) => addressCase.raw),
    ...REGION_CASES.map((regionCase) => regionCase.raw),
    'Москва, Москва',
    'Республика Татарстан (Татарстан)',
    '115569, ул.Шипиловский, д.37 корпус 2, Москва, Москва, Москва',
    'X (a) (b)',
    '(Татарстан)',
    '',
    '   ',
    '115569, ул.1‑я Аэропортовская, д.1, Москва',
    'Школа № 878 (корпус 2)',
    'Кемеровская область - Кузбасс',
    // T.Y19 — the named-after particle in all three spellings + the bare street type +
    // a ё/е pair: the folds of normalize.ts §«NAME_PARTICLE» / §`ё → е` must be idempotent
    // on exactly the spellings the owner's live-testing finding quotes.
    'ул.им Е.И.Пугачёва, д.123, Саратов',
    'ул.им.Е.И.Пугачёва, д.123, Саратов',
    '410054, ул.имени Е.И.Пугачёва, д.123, Саратов',
    'ул.им Валерия Заволянского, д.1, Яблоновский',
    '385140, ул.имени Валерия Заволянского, д.1, Яблоновский, Республика Адыгея',
    'Москва, улица имени Ленина, д.1',
    'ул.Пугачева, д.3',
    'ул.Пугачёва, д.3',
]);
/* ------------------------------------------------------------------ *
 * Deterministic PRNG for the idempotency property test (no dependencies).
 * ------------------------------------------------------------------ */
/** mulberry32 — 32-bit, reproducible across hosts, enough for 20 seeds. */
export function mulberry32(seed) {
    let state = seed >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Mutations that mimic what Google Sheets / Excel / humans do to a cell (docs/02 §10.3). */
const MUTATIONS = Object.freeze([
    (v) => v.toUpperCase(),
    (v) => v.toLowerCase(),
    (v, rnd) => (rnd() < 0.5 ? v.replace(/, /g, ',') : v.replace(/, /g, ' , ')),
    (v, rnd) => (rnd() < 0.5 ? `${v}, ${v.split(',')[0]?.trim() ?? ''}` : v),
    (v) => `${v} (татарстан)`,
    (v) => `  ${v}  `,
    (v) => `${v}.`,
    (v) => v.replace(/ул\./g, 'ул. '),
    (v) => v.replace(/д\.(\d)/g, 'д. $1'),
    (v) => v.replace(/г\. /g, 'г.'),
    (v) => v.replace(/[‐‑]/g, '-'),
    (v) => v.replace(/-/g, '‑'),
    (v, rnd) => v.split('').map((ch) => (ch === ' ' && rnd() < 0.2 ? ' ' : ch)).join(''),
    (v) => v.replace(/к\.(\d)/g, 'корпус $1'),
    (v) => v.replace(/корпус (\d)/g, 'к.$1'),
]);
/**
 * 20 deterministic seeds (the AC: "idempotency property tests (random 20 seeds)").
 * Same seeds → same inputs on every host and in both runners.
 */
export function idempotencySeeds(count = 20) {
    const seeds = [];
    for (let i = 0; i < count; i += 1) {
        const rnd = mulberry32(0xc0ffee + i);
        const source = IDEMPOTENCY_CORPUS[Math.floor(rnd() * IDEMPOTENCY_CORPUS.length)] ?? '';
        let mutated = source;
        const rounds = 1 + Math.floor(rnd() * 3);
        for (let r = 0; r < rounds; r += 1) {
            const mutation = MUTATIONS[Math.floor(rnd() * MUTATIONS.length)];
            mutated = mutation ? mutation(mutated, rnd) : mutated;
        }
        seeds.push({ seed: i, source, mutated });
    }
    return seeds;
}
//# sourceMappingURL=cases.js.map